'use client';

import { useState, useEffect, useRef } from 'react';
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from 'framer-motion';
import BlogHero from './components/blog-hero';
import BlogFeatured from './components/blog-featured';
import BlogGrid from './components/blog-grid';
import BlogSearch from './components/blog-search';
import BlogSEOContent from './components/blog-content-seo';

export default function BlogLayout() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [dateRange, setDateRange] = useState([null, null]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const containerRef = useRef(null);

  // Parallax effect for the entire page
  const { scrollY } = useScroll();
  const backgroundY = useTransform(scrollY, [0, 500], [0, 100]);

  useEffect(() => {
    setIsLoaded(true);

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All');
    setDateRange([null, null]);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  // Animated background elements
  const backgroundElements = Array.from({ length: 15 }).map((_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: 5 + Math.random() * 20,
    duration: 15 + Math.random() * 30,
    delay: Math.random() * 2,
    opacity: 0.03 + Math.random() * 0.07,
    shape: Math.random() > 0.7 ? 'square' : 'circle',
  }));

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className='sr-only'
      >
        <BlogSEOContent />
      </motion.div>

      <AnimatePresence>
        {isLoaded && (
          <motion.div
            ref={containerRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className='min-h-screen bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-950 relative overflow-hidden'
          >
            {/* Animated background elements */}
            {backgroundElements.map((element) => (
              <motion.div
                key={element.id}
                className={`absolute ${element.shape === 'square' ? 'rounded-lg' : 'rounded-full'} bg-primarycolor dark:bg-secondarycolor`}
                style={{
                  left: `${element.x}%`,
                  top: `${element.y}%`,
                  width: element.size,
                  height: element.size,
                  opacity: element.opacity,
                  y: backgroundY,
                }}
                animate={{
                  y: [0, -20, 0],
                  x: [0, 10, 0],
                  rotate: [0, element.shape === 'square' ? 45 : 0, 0],
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  repeat: Number.POSITIVE_INFINITY,
                  duration: element.duration,
                  delay: element.delay,
                  ease: 'easeInOut',
                }}
              />
            ))}

            <BlogHero />

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className='container mx-auto px-4 sm:px-6 lg:px-8 py-10 relative z-10'
            >
              <BlogFeatured />

              <div className='mt-16'>
                <BlogSearch
                  searchQuery={searchQuery}
                  selectedCategory={selectedCategory}
                  dateRange={dateRange}
                  onSearch={handleSearch}
                  onCategoryChange={handleCategoryChange}
                  onDateRangeChange={handleDateRangeChange}
                  onResetFilters={handleResetFilters}
                />

                <BlogGrid
                  searchQuery={searchQuery}
                  selectedCategory={selectedCategory}
                  dateRange={dateRange}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
